import {
  consumerAddressCombinations,
  consumerAddressMinimumFields,
  consumerNameCombinations,
  consumerNameMinimumsFields,
  checkAddressCompleteness,
} from '../../validators';
import { InputLayoutField } from '../../../../program';
import { collectionUtils } from '../../../../../utils';
import * as yup from 'yup';

export const inputFieldValidator = yup.object<InputLayoutField>({
  name: yup
    .string()
    .label('Experian Header')
    .notRequired()
    .test(
      'InvalidChars',
      'Invalid characters in the header name',
      function validCharacters(currentField) {
        if (currentField?.length < 1) {
          return true;
        }
        if (!/^[0-9a-zA-Z_ ]+$/.test(currentField)) {
          return this.createError({
            path: this.path,
            message: `Invalid characters in the header name: ${currentField}. Accepted characters: alphabet, numbers, underscore(_) and spaces.`,
          });
        } else {
          return true;
        }
      },
    ),
  fieldLength: yup.number().label('Field Length').required().min(1).max(1000),
  headerName: yup.string().label('Client Header').notRequired(),
});

const getField = (
  input: string | InputLayoutField,
  nameExtractorFn: (field: InputLayoutField) => string = headerNameExtractorFn,
): string => (typeof input === 'string' ? input : nameExtractorFn(input));

const getFields = (
  input: (string | InputLayoutField)[],
  nameExtractorFn: (field: InputLayoutField) => string = headerNameExtractorFn,
): string[] => input.map((a) => getField(a, nameExtractorFn));

const inputFieldsConsumerAddressCombinationsValidator = yup
  .array<string | InputLayoutField>()
  .test({
    name: 'ConsumerAddressCombinations',
    test(fields: (string | InputLayoutField)[]) {
      const { path, createError } = this;
      const fieldNamesWithIndex = getFields(fields).map((a, index) => ({
        index,
        fieldName: a,
      }));

      const fieldNames = fieldNamesWithIndex
        .map((a) => a.fieldName)
        .filter((a) => a !== null && a !== undefined);

      if (fieldNamesWithIndex.length === 0) {
        return true;
      } else {
        const errors: yup.ValidationError[] = [];
        fieldNamesWithIndex.forEach(({ fieldName, index }) => {
          const combinations = consumerAddressCombinations.find(
            (a) => a.fieldName.toLowerCase() === fieldName.toLowerCase(),
          );
          // const shouldNotHaveFields: string[] = [];
          if (combinations) {
            const shouldNotHaveFields = fieldNames.filter((a) =>
              combinations.shouldNotExistList.includes(a.toLowerCase()),
            );
            if (shouldNotHaveFields.length > 0) {
              errors.push(
                createError({
                  path: `${path}[${index}].name`,
                  message: `Cannot select the fields ${shouldNotHaveFields.join(
                    ', ',
                  )} when the field ${fieldNames[index]} is selected`,
                }),
              );
            }
          }
        });
        return errors.length === 0
          ? true
          : this.createError({
              path: `${this.path}Errors.addressCombinations`,
            });
      }
    },
  });

const inputFieldsConsumerAddressCompletenessValidator = yup
  .array<string | InputLayoutField>()
  .test({
    name: 'ConsumerAddressCompleteness',
    test: async (inputFields: (string | InputLayoutField)[]) => {
      const fields = getFields(inputFields);
      const response = await checkAddressCompleteness(fields, addressValidator);
      return response.length === 0;
    },
  });

const inputFieldsConsumerNameCombinationsValidator = yup
  .array<string | InputLayoutField>()
  .test({
    name: 'ConsumerNameCombinations',
    test(fields: (string | InputLayoutField)[]) {
      const { path, createError } = this;
      const fieldNamesWithIndex = getFields(fields).map((a, index) => ({
        index,
        fieldName: a,
      }));

      const fieldNames = fieldNamesWithIndex
        .map((a) => a.fieldName)
        .filter((a) => a !== null && a !== undefined);
      // console.log('ConsumerNameCombinations: ', fieldNames);
      if (fieldNamesWithIndex.length === 0) {
        return true;
      } else {
        const errors: yup.ValidationError[] = [];
        fieldNamesWithIndex.forEach(({ fieldName, index }) => {
          const combinations = consumerNameCombinations.find(
            (a) => a.fieldName.toLowerCase() === fieldName.toLowerCase(),
          );
          // const shouldNotHaveFields: string[] = [];
          if (combinations) {
            const shouldNotHaveFields = fieldNames.filter((a) =>
              combinations.shouldNotExistList.includes(a.toLowerCase()),
            );
            if (shouldNotHaveFields.length > 0) {
              errors.push(
                createError({
                  path: `${path}[${index}].name`,
                  message: `Cannot select the fields ${shouldNotHaveFields.join(
                    ', ',
                  )} when the field ${fieldNames[index]} is selected`,
                }),
              );
            }
          }
        });

        const toReturn =
          errors.length === 0
            ? true
            : this.createError({
                path: `${this.path}Errors.nameCombinations`,
              });
        // console.log('NameCombinations: ', errors, toReturn);
        return toReturn;
      }
    },
  });

const headerNameExtractorFn = (field: InputLayoutField): string =>
  field.name || '';

const inputFieldsConsumerAddressMinimumsValidator = yup
  .array<string | InputLayoutField>()
  .test({
    name: 'ConsumerAddressMinimums',
    test(fields: (string | InputLayoutField)[]) {
      const { path, createError } = this;
      const effectiveFields = getFields(fields, headerNameExtractorFn);
      // console.log('In consumerAddressMinimums: ', effectiveFields);
      for (const fieldsSet of consumerAddressMinimumFields) {
        const localValid = fieldsSet.every((b) =>
          effectiveFields.some((c) => {
            const toReturn = c?.toLowerCase() === b.toLowerCase();
            return toReturn;
          }),
        );
        if (localValid) {
          return true;
        }
      }
      // console.log(`In consumerAddressMinimums soo:  ${path}Errors`);
      const errorMessage = `At least one of the field combinations below needs to be selected\\n${consumerAddressMinimumFields
        .map((a) => a.join(', '))
        .join('\\n')}`;
      return createError({
        path: `${path}Errors.addressMinimums`,
        message: errorMessage,
      });
    },
  });

const inputFieldsConsumerNameMinimumsValidator = yup
  .array<string | InputLayoutField>()
  .test({
    name: 'ConsumerNameMinimums',
    test(fields: (string | InputLayoutField)[]) {
      const { path, createError } = this;

      const effectiveFields = getFields(fields, headerNameExtractorFn);

      for (const fieldsSet of consumerNameMinimumsFields) {
        const localValid = fieldsSet.every((b) =>
          effectiveFields.some((c) => {
            const toReturn = c?.toLowerCase() === b.toLowerCase();
            return toReturn;
          }),
        );
        if (localValid) {
          return true;
        }
      }
      // console.log('In consumerNameMinimums: ', effectiveFields);

      const errorMessage = `At least one of the field combinations below needs to be selected\\n${consumerNameMinimumsFields
        .map((a) => a.join(', '))
        .join('\\n')}`;
      return createError({
        path: `${path}Errors.nameMinimums`,
        message: errorMessage,
      });
    },
  });

export const addressValidator = yup
  .array<string | InputLayoutField>()
  .default([])
  .transform((values) => values || [])
  .concat(inputFieldsConsumerAddressMinimumsValidator)
  .concat(inputFieldsConsumerAddressCombinationsValidator);

export const inputFieldsCombinationsValidator = yup
  .array<string | InputLayoutField>()
  .default([])
  .transform((values) => {
    return values || [];
  })
  .concat(addressValidator)
  .concat(inputFieldsConsumerNameMinimumsValidator)
  .concat(inputFieldsConsumerNameCombinationsValidator)
  .concat(inputFieldsConsumerAddressCompletenessValidator);

export const inputFieldsValidator: yup.NotRequiredArraySchema<InputLayoutField> =
  yup
    .array<InputLayoutField>(inputFieldValidator.required())
    .default([])
    .transform((values) => {
      return values || [];
    })
    .test(
      'unique',
      'Field Headers must be unique.',
      function validateUnique(currentFields: InputLayoutField[]) {
        // console.log('currentFields: ', currentFields);
        const effectiveFields = currentFields.map((a) => a.name || '');
        if (effectiveFields.length < 1) {
          return true;
        }
        // console.log(this.parent.filter, this.parent, this);
        const grouped = collectionUtils.groupBy(
          currentFields,
          (a) => (a.name || '').toLowerCase(),
          (a) => a.name || '',
        );
        const sameFields = Object.values(grouped)
          .filter((a) => a.length > 1 && a[0] !== '')
          .map((a) => a[0]);
        const sameFieldNames = sameFields.join(', ');
        return sameFields.length > 0
          ? this.createError({
              path: `${this.path}`,
              message: `Field Headers must be unique. ${sameFieldNames} occur more than once.`,
            })
          : true;
      },
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .concat(inputFieldsCombinationsValidator as any);
